import { UserOutlined } from '@ant-design/icons';
import type { ProLayoutProps, Settings as LayoutSettings } from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import { history } from '@umijs/max';
import React from 'react';
import { AvatarDropdown, AvatarName, Footer } from '@/components';
import { currentUser as queryCurrentUser } from '@/services';
import { getToken, clearToken } from '@/utils/auth';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';

const loginPath = '/user/login';

type LayoutMenuData = Parameters<NonNullable<ProLayoutProps['menuDataRender']>>[0];
type LayoutMenuItem = LayoutMenuData[number] & {
    disabledTooltip?: boolean;
    children?: LayoutMenuItem[];
};

const disableMenuItemTooltips = (menuData: LayoutMenuData): LayoutMenuData => {
    return menuData.map((item) => {
        const menuItem = item as LayoutMenuItem;

        return {
            ...menuItem,
            disabledTooltip: true,
            children: menuItem.children
                ? disableMenuItemTooltips(menuItem.children as LayoutMenuData)
                : menuItem.children,
        };
    }) as LayoutMenuData;
};

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 */
export async function getInitialState(): Promise<{
    settings?: Partial<LayoutSettings>;
    currentUser?: API.CurrentUser;
    loading?: boolean;
    fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
}> {
    const fetchUserInfo = async () => {
        try {
            const msg = await queryCurrentUser({
                skipErrorHandler: true,
            });
            return msg.data;
        } catch (error) {
            clearToken();
            history.push(loginPath);
        }
        return undefined;
    };

    // 非登录页，需要校验 token
    const { location } = history;
    if (location.pathname !== loginPath) {
        const token = getToken();
        if (!token) {
            history.push(loginPath);
            return {
                fetchUserInfo,
                settings: defaultSettings as Partial<LayoutSettings>,
            };
        }

        const currentUser = await fetchUserInfo();
        return {
            fetchUserInfo,
            currentUser,
            settings: defaultSettings as Partial<LayoutSettings>,
        };
    }
    return {
        fetchUserInfo,
        settings: defaultSettings as Partial<LayoutSettings>,
    };
}

// ProLayout 支持的 api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
    initialState,
    setInitialState,
}) => {
    return {
        avatarProps: {
            src: initialState?.currentUser?.Avatar || undefined,
            size: 'small',
            icon: <UserOutlined />,
            title: <AvatarName />,
            render: (_, avatarChildren) => {
                return <AvatarDropdown>{avatarChildren}</AvatarDropdown>;
            },
        },
        waterMarkProps: {
            content: initialState?.currentUser?.Name,
            fontSize: 14,
            gap: [300, 300],
        },
        footerRender: () => <Footer />,
        onPageChange: () => {
            const { location } = history;
            // 如果没有登录，重定向到 login
            if (!initialState?.currentUser && location.pathname !== loginPath) {
                history.push(loginPath);
            }
        },
        menuHeaderRender: undefined,
        menuDataRender: disableMenuItemTooltips,
        childrenRender: (children) => {
            return (
                <>
                    {children}
                    {process.env.UMI_APP_ENABLE_SETTING_DRAWER === 'true' && (
                        <SettingDrawer
                            disableUrlParams
                            enableDarkTheme
                            hideHintAlert
                            hideCopyButton
                            settings={initialState?.settings}
                            onSettingChange={(settings) => {
                                setInitialState((preInitialState) => ({
                                    ...preInitialState,
                                    settings,
                                }));
                            }}
                        />
                    )}
                </>
            );
        },
        ...initialState?.settings,
    };
};

/**
 * @name request 配置
 */
const isDev = process.env.NODE_ENV === 'development';

export const request: RequestConfig = {
    baseURL: isDev ? '' : (process.env.UMI_APP_API_BASE_URL || ''),
    ...errorConfig,
};
